import React, { useState, useRef } from 'react';
import { Container, Card, Row, Col, Form, Button, Alert, Badge, Table, ProgressBar, Modal, Spinner } from 'react-bootstrap';
import { PlusIcon, TrashIcon, SparklesIcon, PlayIcon, CheckCircleIcon, XCircleIcon, ClockIcon, FireIcon } from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { useCompanyBatchOptimization } from './hooks/useCompanyBatchOptimization';

const categoryColors: Record<string, string> = {
  prep: '#6366f1',
  mixing: '#0891b2',
  ferment: '#d97706',
  shape: '#059669',
  proof: '#7c3aed',
  bake: '#dc2626',
};

const thStyle: React.CSSProperties = {
  color: '#475569',
  fontWeight: 600,
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: '12px 16px',
};

const sectionLabel: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#9ca3af',
  marginBottom: '14px',
  paddingBottom: '8px',
  borderBottom: '1px solid #f1f5f9',
};

function fmtQty(quantity: number | string, unit: string): string {
  const q = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
  if (isNaN(q)) return `${quantity} ${unit}`;
  const u = (unit || '').toLowerCase().trim();
  if (u === 'g' && q >= 1000) return `${parseFloat((q / 1000).toFixed(3).replace(/\.?0+$/, ''))} kg`;
  if (u === 'ml' && q >= 1000) return `${parseFloat((q / 1000).toFixed(3).replace(/\.?0+$/, ''))} l`;
  const display = q % 1 === 0 ? q.toLocaleString() : parseFloat(q.toFixed(3)).toLocaleString();
  return `${display} ${unit}`;
}

export default function CompanyBatchOptimization() {
  const {
    recipes, starters,
    productionItems, addProductionItem, removeProductionItem, updateProductionItem,
    equipment, updateEquipment,
    staff, addStaff, removeStaff, updateStaff,
    starterInventory, addStarterItem, removeStarterItem, updateStarterItem,
    result, loading, error, setError,
    activeTab, setActiveTab,
    runOptimization,
    productions, productionLoading, productionError, setProductionError, lowStockAlerts, setLowStockAlerts,
    startProduction, updateProductionStatus,
    staffMembers,
  } = useCompanyBatchOptimization();

  const tabs = ['overview', 'timeline', 'batches', 'ingredients', 'starter'] as const;
  const tabLabels: Record<string, string> = {
    overview: 'Overview',
    timeline: 'Timeline',
    batches: 'Mixer Batches',
    ingredients: 'Ingredient List',
    starter: 'Starter Plan',
  };

  // Start Production modal state
  const [showStartModal, setShowStartModal] = useState(false);
  const [prodDate, setProdDate] = useState(new Date().toISOString().slice(0, 10));
  const [prodName, setProdName] = useState('');
  const [prodResponsible, setProdResponsible] = useState<number | ''>('');
  const [lastCreatedProduction, setLastCreatedProduction] = useState<{ id: number; name: string } | null>(null);
  const [productionStarted, setProductionStarted] = useState(false);
  const [cancelModal, setCancelModal] = useState<{ show: boolean; production: typeof productions[0] | null }>({ show: false, production: null });
  const productionsSectionRef = useRef<HTMLDivElement>(null);

  const today = new Date().toISOString().slice(0, 10);

  const statusConfig: Record<string, { color: string; bg: string; icon: React.ReactNode; label: string }> = {
    waiting:   { color: '#92400e', bg: '#fef3c7', icon: <ClockIcon style={{ width: 12, height: 12 }} />, label: 'Waiting' },
    started:   { color: '#1d4ed8', bg: '#dbeafe', icon: <PlayIcon style={{ width: 12, height: 12 }} />, label: 'Started' },
    completed: { color: '#065f46', bg: '#d1fae5', icon: <CheckCircleIcon style={{ width: 12, height: 12 }} />, label: 'Completed' },
    cancelled: { color: '#7f1d1d', bg: '#fee2e2', icon: <XCircleIcon style={{ width: 12, height: 12 }} />, label: 'Cancelled' },
  };

  const nextStatuses: Record<string, { value: string; label: string; variant: string }[]> = {
    waiting:   [{ value: 'started', label: 'Mark Started', variant: 'outline-primary' }, { value: 'cancelled', label: 'Cancel', variant: 'outline-danger' }],
    started:   [{ value: 'completed', label: 'Mark Completed', variant: 'outline-success' }, { value: 'cancelled', label: 'Cancel', variant: 'outline-danger' }],
    completed: [],
    cancelled: [],
  };

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
            <div style={{ backgroundColor: '#ecfdf5', borderRadius: '8px', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SparklesIcon style={{ width: '22px', height: '22px', color: '#059669' }} />
            </div>
            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Batch Optimizer</h4>
          </div>
          <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
            Given everything I need to make tomorrow — what's the most efficient way to do it?
          </p>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
            {error}
          </Alert>
        )}

        <Row className="g-4">
          {/* Left column: Setup */}
          <Col xs={12} lg={5}>

            {/* Production Requirements */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>Production Requirements</div>
                <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>
                  Select recipes and how many batches to produce.
                </p>
                {productionItems.map((item, idx) => (
                  <div key={idx} className="mb-3">
                    <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>Recipe</Form.Label>
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Select
                        value={item.recipe_id}
                        onChange={e => updateProductionItem(idx, 'recipe_id', e.target.value ? parseInt(e.target.value) : '')}
                        style={{ border: '1px solid #e2e8f0', borderRadius: '8px', flex: 1 }}
                      >
                        <option value="">Select recipe…</option>
                        {recipes.map((r: any) => (
                          <option key={r.id} value={r.id}>
                            {r.name}{r.total_yield_g ? ` — ${r.total_yield_g}g/batch` : ''}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control
                        type="number"
                        min={1}
                        step={1}
                        value={item.quantity}
                        title="Batches"
                        onChange={e => updateProductionItem(idx, 'quantity', parseFloat(e.target.value) || 1)}
                        style={{ border: '1px solid #e2e8f0', borderRadius: '8px', width: '70px', flexShrink: 0 }}
                      />
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeProductionItem(idx)}
                        disabled={productionItems.length === 1}
                        style={{ borderRadius: '8px', padding: '6px 10px', flexShrink: 0 }}
                      >
                        <TrashIcon style={{ width: '14px', height: '14px' }} />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button variant="outline-success" size="sm" onClick={addProductionItem} style={{ borderRadius: '8px' }}>
                  <PlusIcon style={{ width: '13px', height: '13px', marginRight: '4px' }} />
                  Add Recipe
                </Button>
              </Card.Body>
            </Card>

            {/* Equipment */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
              <Card.Body className="p-4">
                <div style={sectionLabel}>Equipment</div>
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>Mixer max capacity (kg)</Form.Label>
                    <Form.Control
                      type="number" min={1} step={1}
                      value={equipment.mixer_max_kg}
                      onChange={e => updateEquipment('mixer_max_kg', parseFloat(e.target.value) || 20)}
                      style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    />
                  </Col>
                  <Col xs={6}>
                    <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>Oven capacity (pieces/load)</Form.Label>
                    <Form.Control
                      type="number" min={1} step={1}
                      value={equipment.oven_capacity}
                      onChange={e => updateEquipment('oven_capacity', parseInt(e.target.value) || 8)}
                      style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    />
                  </Col>
                  <Col xs={6}>
                    <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>Proofer capacity (pieces)</Form.Label>
                    <Form.Control
                      type="number" min={1} step={1}
                      value={equipment.proofer_capacity}
                      onChange={e => updateEquipment('proofer_capacity', parseInt(e.target.value) || 20)}
                      style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Staff */}
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ ...sectionLabel, marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Staff Shifts</div>
                  <Button variant="outline-secondary" size="sm" onClick={addStaff} style={{ borderRadius: '8px', fontSize: '0.78rem' }}>
                    <PlusIcon style={{ width: '12px', height: '12px', marginRight: '3px' }} />
                    Add
                  </Button>
                </div>
                {staff.length === 0 ? (
                  <p className="text-muted mb-0" style={{ fontSize: '0.82rem' }}>No staff added — optimizer assumes 8:00 AM start.</p>
                ) : staff.map((s, idx) => (
                  <div key={idx} className="mb-3">
                    <div className="d-flex gap-2 align-items-center mb-1">
                      <Form.Control
                        placeholder="Name"
                        value={s.name}
                        onChange={e => updateStaff(idx, 'name', e.target.value)}
                        style={{ border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.88rem' }}
                      />
                      <Button variant="outline-danger" size="sm" onClick={() => removeStaff(idx)} style={{ borderRadius: '8px', padding: '6px 10px', flexShrink: 0 }}>
                        <TrashIcon style={{ width: '13px', height: '13px' }} />
                      </Button>
                    </div>
                    <div className="d-flex gap-2">
                      <div style={{ flex: 1 }}>
                        <Form.Label style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>Start</Form.Label>
                        <Form.Control type="time" value={s.start_time} onChange={e => updateStaff(idx, 'start_time', e.target.value)} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <Form.Label style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>End</Form.Label>
                        <Form.Control type="time" value={s.end_time} onChange={e => updateStaff(idx, 'end_time', e.target.value)} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem' }} />
                      </div>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>

            {/* Starter Inventory */}
            {starters.length > 0 && (
              <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ ...sectionLabel, marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Starter Inventory</div>
                    <Button variant="outline-secondary" size="sm" onClick={addStarterItem} style={{ borderRadius: '8px', fontSize: '0.78rem' }}>
                      <PlusIcon style={{ width: '12px', height: '12px', marginRight: '3px' }} />
                      Add
                    </Button>
                  </div>
                  {starterInventory.length === 0 ? (
                    <p className="text-muted mb-0" style={{ fontSize: '0.82rem' }}>Add your current starter weights for an accurate feeding plan.</p>
                  ) : starterInventory.map((s, idx) => (
                    <div key={idx} className="d-flex gap-2 mb-2 align-items-center">
                      <Form.Select
                        value={s.starter_id}
                        onChange={e => updateStarterItem(idx, 'starter_id', e.target.value ? parseInt(e.target.value) : '')}
                        style={{ border: '1px solid #e2e8f0', borderRadius: '8px', flex: 1 }}
                      >
                        <option value="">Select starter…</option>
                        {starters.map((st: any) => <option key={st.id} value={st.id}>{st.name}</option>)}
                      </Form.Select>
                      <Form.Control
                        type="number" min={0} placeholder="g"
                        value={s.current_weight_g}
                        onChange={e => updateStarterItem(idx, 'current_weight_g', parseFloat(e.target.value) || 0)}
                        style={{ border: '1px solid #e2e8f0', borderRadius: '8px', width: '80px', flexShrink: 0 }}
                      />
                      <Button variant="outline-danger" size="sm" onClick={() => removeStarterItem(idx)} style={{ borderRadius: '8px', padding: '6px 10px', flexShrink: 0 }}>
                        <TrashIcon style={{ width: '13px', height: '13px' }} />
                      </Button>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            )}

            {/* Generate Button */}
            <Button
              variant="success"
              onClick={() => { setProductionStarted(false); runOptimization(); }}
              disabled={loading}
              className="w-100 d-flex align-items-center justify-content-center gap-2"
              style={{ padding: '12px', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 700 }}
            >
              <SparklesIcon style={{ width: '16px', height: '16px' }} />
              {loading ? 'Generating plan…' : 'Generate Optimization Plan'}
            </Button>
          </Col>

          {/* Right column: Results */}
          <Col xs={12} lg={7}>
            {!result ? (
              <Card className="border-0 shadow-sm" style={{ borderRadius: '10px', minHeight: '300px' }}>
                <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                  <div style={{ backgroundColor: '#ecfdf5', borderRadius: '50%', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                    <SparklesIcon style={{ width: '30px', height: '30px', color: '#059669' }} />
                  </div>
                  <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '8px' }}>Ready to optimize</div>
                  <p className="text-muted mb-0" style={{ fontSize: '0.85rem', maxWidth: '300px' }}>
                    Configure your requirements on the left, then click Generate.
                  </p>
                </Card.Body>
              </Card>
            ) : (
              <>
                {/* Tabs */}
                <div className="d-flex gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
                  {tabs.map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      style={{
                        padding: '6px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                        fontSize: '0.83rem', fontWeight: activeTab === tab ? 700 : 400,
                        backgroundColor: activeTab === tab ? '#059669' : '#f1f5f9',
                        color: activeTab === tab ? 'white' : '#374151',
                        transition: 'all 0.15s',
                      }}
                    >
                      {tabLabels[tab]}
                    </button>
                  ))}
                </div>

                {/* Overview */}
                {activeTab === 'overview' && (
                  <>
                    <Row className="g-3 mb-3">
                      {[
                        { label: 'Total Dough', value: `${result.summary.total_dough_kg} kg` },
                        { label: 'Mixer Loads', value: result.summary.mixer_loads },
                        { label: 'Oven Loads', value: result.summary.oven_loads },
                        { label: result.summary.pieces_label || 'Est. Pieces', value: result.summary.estimated_pieces },
                        { label: 'Labor', value: `${result.summary.total_labor_hours}h` },
                        { label: 'Recipes', value: result.summary.total_recipes },
                      ].map(({ label, value }) => (
                        <Col xs={4} key={label}>
                          <Card className="border-0 shadow-sm text-center" style={{ borderRadius: '10px' }}>
                            <Card.Body className="py-3 px-2">
                              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#059669' }}>{value}</div>
                              <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 500 }}>{label}</div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                    <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
                      <Card.Body className="p-4">
                        <div style={sectionLabel}>Labor Breakdown</div>
                        <Row className="g-3">
                          {[
                            { label: 'Mixing', min: result.labor.mixing_min },
                            { label: 'Shaping', min: result.labor.shaping_min },
                            { label: 'Baking', min: result.labor.baking_min },
                            { label: 'Cleaning', min: result.labor.cleaning_min },
                          ].map(({ label, min }) => (
                            <Col xs={6} md={3} key={label}>
                              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '3px' }}>{label}</div>
                              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
                                {min >= 60 ? `${Math.floor(min / 60)}h ${min % 60}m` : `${min}m`}
                              </div>
                            </Col>
                          ))}
                        </Row>
                        <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #e2e8f0', fontWeight: 700, color: '#059669', fontSize: '0.9rem' }}>
                          Total: {result.summary.total_labor_hours} hours
                        </div>
                      </Card.Body>
                    </Card>
                    {/* Success feedback after production created */}
                    {lastCreatedProduction && (
                      <Alert variant="success" dismissible onClose={() => setLastCreatedProduction(null)} className="mb-3" style={{ borderRadius: '10px', fontSize: '0.88rem' }}>
                        ✓ <strong>{lastCreatedProduction.name}</strong> has been created. <a href="#productions-section" onClick={e => { e.preventDefault(); productionsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} style={{ color: '#065f46', fontWeight: 600 }}>View in list ↓</a>
                      </Alert>
                    )}

                    {/* Waste warnings */}
                    {result.waste_warnings.length > 0 && (
                      <Alert variant="warning" style={{ borderRadius: '10px' }}>
                        <div style={{ fontWeight: 700, marginBottom: '8px' }}>⚠ Optimization Suggestions</div>
                        {result.waste_warnings.map((w, i) => (
                          <div key={i} className="mb-2">
                            <div style={{ fontSize: '0.87rem' }}>{w.message}</div>
                            <div style={{ fontSize: '0.81rem', color: '#92400e' }}>{w.suggestion}</div>
                          </div>
                        ))}
                      </Alert>
                    )}
                    {/* Start Production CTA */}
                    {productionStarted ? (
                      <Button
                        variant="outline-success"
                        disabled
                        className="w-100 d-flex align-items-center justify-content-center gap-2 mt-2"
                        style={{ padding: '12px', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 700, opacity: 0.75, cursor: 'not-allowed' }}
                      >
                        <CheckCircleIcon style={{ width: '17px', height: '17px' }} />
                        Sent to Production
                      </Button>
                    ) : (
                      <Button
                        variant="success"
                        className="w-100 d-flex align-items-center justify-content-center gap-2 mt-2"
                        style={{ padding: '12px', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 700 }}
                        onClick={() => {
                          const dateStr = today.replace(/-/g, '');
                          const num = String(productions.length + 1).padStart(3, '0');
                          const autoNum = `PRD-${dateStr}-${num}`;
                          const recipeDesc = productionItems.filter(i => i.recipe_id && i.quantity > 0).map(i => {
                            const r = recipes.find((rec: any) => rec.id === Number(i.recipe_id));
                            return r ? `${r.name} ×${i.quantity}` : '';
                          }).filter(Boolean).join(', ');
                          setProdName(recipeDesc ? `${autoNum} — ${recipeDesc}` : autoNum);
                          setProdDate(today);
                          setShowStartModal(true);
                        }}
                      >
                        <PlayIcon style={{ width: '17px', height: '17px' }} />
                        Start Production
                      </Button>
                    )}
                  </>
                )}

                {/* Timeline */}
                {activeTab === 'timeline' && (
                  <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                    <Card.Body className="p-4">
                      <div style={sectionLabel}>Production Timeline</div>
                      <div style={{ position: 'relative', paddingLeft: '24px' }}>
                        {result.timeline.map((step, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: '16px', marginBottom: '20px', position: 'relative' }}>
                            <div style={{ position: 'absolute', left: '-20px', top: '6px', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: categoryColors[step.category] || '#94a3b8' }} />
                            {idx < result.timeline.length - 1 && (
                              <div style={{ position: 'absolute', left: '-16px', top: '16px', width: '2px', height: 'calc(100% + 4px)', backgroundColor: '#e2e8f0' }} />
                            )}
                            <div style={{ minWidth: '90px', fontSize: '0.78rem', fontWeight: 700, color: '#374151', paddingTop: '2px' }}>{step.time}</div>
                            <div>
                              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{step.step}</div>
                              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '2px' }}>{step.note}</div>
                              {step.duration_min > 0 && (
                                <Badge style={{ fontSize: '0.68rem', fontWeight: 500, marginTop: '4px', backgroundColor: categoryColors[step.category] || '#94a3b8', color: 'white', border: 'none' }}>
                                  {step.duration_min >= 60 ? `${Math.floor(step.duration_min / 60)}h ${step.duration_min % 60}m` : `${step.duration_min} min`}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Mixer Batches */}
                {activeTab === 'batches' && result.mixer_batches.map(batch => (
                  <Card key={batch.batch_number} className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
                    <Card.Body className="p-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>Mixer Batch #{batch.batch_number}</div>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>{batch.total_kg} kg</div>
                      </div>
                      <ProgressBar
                        now={batch.utilization_pct}
                        variant={batch.utilization_pct >= 80 ? 'success' : batch.utilization_pct >= 60 ? 'warning' : 'danger'}
                        style={{ height: '6px', borderRadius: '3px', marginBottom: '8px' }}
                      />
                      <div style={{ fontSize: '0.76rem', color: '#6b7280', marginBottom: '12px' }}>{batch.utilization_pct}% of mixer capacity</div>
                      {batch.recipes.map((r, i) => (
                        <div key={i} className="d-flex justify-content-between py-2" style={{ borderTop: '1px solid #f1f5f9' }}>
                          <span style={{ fontSize: '0.88rem', fontWeight: 500 }}>{r.recipe_name}</span>
                          <span style={{ fontSize: '0.88rem', color: '#6b7280' }}>{r.batch_kg} kg</span>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                ))}

                {/* Ingredients */}
                {activeTab === 'ingredients' && (
                  <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                    <div className="table-responsive">
                      <Table hover className="mb-0 align-middle">
                        <thead>
                          <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            <th style={thStyle}>Ingredient</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Quantity</th>
                            <th style={thStyle}>Unit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.ingredient_totals.map((ing, i) => (
                            <tr key={i}>
                              <td style={{ padding: '12px 16px', fontWeight: 500 }}>
                                {ing.name}
                                {ing.is_starter && (
                                  <Badge bg="warning" text="dark" style={{ marginLeft: '8px', fontSize: '0.68rem' }}>starter</Badge>
                                )}
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>
                                {fmtQty(ing.quantity, ing.unit)}
                              </td>
                              <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '0.85rem' }}></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card>
                )}

                {/* Starter Plan */}
                {activeTab === 'starter' && (
                  result.starter_needed_g === 0 ? (
                    <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                      <Card.Body className="p-4 text-center text-muted">No starter required for this production plan.</Card.Body>
                    </Card>
                  ) : (
                    <>
                      <Alert variant="success" style={{ borderRadius: '10px', marginBottom: '16px', fontSize: '0.88rem' }}>
                        Total starter needed: <strong>{result.starter_needed_g.toLocaleString()}g</strong>
                      </Alert>
                      {result.starter_feeds.map((feed, idx) => (
                        <Card key={idx} className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px', borderLeft: '4px solid #059669' }}>
                          <Card.Body className="p-4">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <Badge bg="" style={{ backgroundColor: '#ecfdf5', color: '#059669', fontWeight: 600, fontSize: '0.72rem', marginBottom: '6px' }}>{feed.timing}</Badge>
                                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>{feed.action}</div>
                              </div>
                              <span className="text-muted" style={{ fontSize: '0.83rem', fontWeight: 600 }}>{feed.time_suggestion}</span>
                            </div>
                            <Row className="g-3">
                              <Col xs={4}>
                                <div style={{ fontSize: '0.73rem', color: '#6b7280', marginBottom: '2px' }}>Starting weight</div>
                                <div style={{ fontWeight: 700, color: '#374151' }}>{feed.from_g}g</div>
                              </Col>
                              <Col xs={4}>
                                <div style={{ fontSize: '0.73rem', color: '#6b7280', marginBottom: '2px' }}>Target weight</div>
                                <div style={{ fontWeight: 700, color: '#374151' }}>{feed.to_g}g</div>
                              </Col>
                              <Col xs={4}>
                                <div style={{ fontSize: '0.73rem', color: '#6b7280', marginBottom: '2px' }}>Add</div>
                                <div style={{ fontWeight: 700, color: '#374151', fontSize: '0.85rem' }}>{feed.add_flour_g}g flour + {feed.add_water_g}g water</div>
                              </Col>
                            </Row>
                            <div className="text-muted mt-3" style={{ fontSize: '0.81rem', fontStyle: 'italic' }}>{feed.note}</div>
                          </Card.Body>
                        </Card>
                      ))}
                      {result.oven_schedule.length > 1 && (
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                          <Card.Body className="p-4">
                            <div style={sectionLabel}>Oven Schedule</div>
                            {result.oven_schedule.map(ob => (
                              <div key={ob.batch} className="d-flex justify-content-between py-2" style={{ borderBottom: '1px solid #f1f5f9', fontSize: '0.88rem' }}>
                                <span style={{ fontWeight: 500 }}>Oven Batch #{ob.batch}</span>
                                <span className="text-muted">{ob.pieces} pieces · {ob.duration_min} min</span>
                              </div>
                            ))}
                          </Card.Body>
                        </Card>
                      )}
                    </>
                  )
                )}
              </>
            )}
          </Col>
        </Row>

        {/* ── Production Section ──────────────────────────────── */}
        <div className="mt-5" ref={productionsSectionRef} id="productions-section">
          {/* Section header */}
          <div className="d-flex align-items-center gap-2 mb-3">
            <div style={{ backgroundColor: '#fef3c7', borderRadius: '8px', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FireIcon style={{ width: '20px', height: '20px', color: '#d97706' }} />
            </div>
            <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Production</h5>
          </div>

          {/* Low-stock alerts */}
          {lowStockAlerts.length > 0 && (
            <Alert variant="danger" dismissible onClose={() => setLowStockAlerts([])} className="mb-3" style={{ borderRadius: '10px' }}>
              <strong>⚠ Low stock after this production:</strong>
              <ul className="mb-0 mt-1">
                {lowStockAlerts.map(a => (
                  <li key={a.ingredient_id} style={{ fontSize: '0.87rem' }}>
                    <strong>{a.name}</strong> — {a.current_stock} {a.unit} remaining (critical: {a.critical_amount} {a.unit})
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {productionError && (
            <Alert variant="danger" dismissible onClose={() => setProductionError('')} className="mb-3" style={{ borderRadius: '10px' }}>
              {productionError}
            </Alert>
          )}

          {productions.length === 0 ? (
            <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
              <Card.Body className="text-center py-4 text-muted" style={{ fontSize: '0.88rem' }}>
                No production runs yet. Generate a plan and click <strong>Start Production</strong>.
              </Card.Body>
            </Card>
          ) : (
            <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ ...thStyle, width: '60px' }}>#</th>
                      <th style={thStyle}>Date</th>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Ingredients</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productions.map((prod, idx) => {
                      const cfg = statusConfig[prod.status] || statusConfig.waiting;
                      const actions = nextStatuses[prod.status] || [];
                      return (
                        <tr key={prod.id}>
                          <td style={{ padding: '12px 16px', fontSize: '0.78rem', fontWeight: 700, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                            #{productions.length - idx}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '0.85rem', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                            {prod.production_date}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '0.88rem', fontWeight: 500, maxWidth: 220 }}>
                            {prod.name || `Production #${prod.id}`}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700,
                              color: cfg.color, backgroundColor: cfg.bg,
                            }}>
                              {cfg.icon} {cfg.label}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '0.8rem', color: '#6b7280', maxWidth: 260 }}>
                            {prod.ingredients.slice(0, 4).map((ing, i) => (
                              <span key={i}>
                                {i > 0 && ', '}{ing.ingredient_name} {fmtQty(ing.quantity, ing.unit)}
                              </span>
                            ))}
                            {prod.ingredients.length > 4 && <span> +{prod.ingredients.length - 4} more</span>}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {actions.map(act => (
                              <Button
                                key={act.value}
                                variant={act.variant as any}
                                size="sm"
                                className="ms-1"
                                style={{ borderRadius: '7px', fontSize: '0.78rem' }}
                                onClick={() => {
                                  if (act.value === 'cancelled') {
                                    setCancelModal({ show: true, production: prod });
                                  } else {
                                    updateProductionStatus(prod.id, act.value);
                                  }
                                }}
                              >
                                {act.label}
                              </Button>
                            ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card>
          )}
        </div>

        {/* ── Cancel Confirmation Modal ────────────────────────── */}
        <Modal show={cancelModal.show} onHide={() => setCancelModal({ show: false, production: null })} centered>
          <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0' }}>
            <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 700, color: '#991b1b' }}>
              <XCircleIcon style={{ width: '18px', height: '18px', marginRight: '8px', color: '#dc2626' }} />
              Cancel Production
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ fontSize: '0.88rem', color: '#374151' }}>
              Are you sure you want to cancel <strong>{cancelModal.production?.name || `Production #${cancelModal.production?.id}`}</strong>?
            </p>
            {cancelModal.production && cancelModal.production.inventory_deducted && cancelModal.production.ingredients.filter(i => i.ingredient !== null).length > 0 && (
              <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px', fontSize: '0.82rem' }}>
                <div style={{ fontWeight: 700, color: '#166534', marginBottom: '8px' }}>The following will be returned to inventory:</div>
                {cancelModal.production.ingredients.filter(i => i.ingredient !== null).map((ing, i) => (
                  <div key={i} style={{ color: '#166534' }}>{ing.ingredient_name} — {fmtQty(ing.quantity, ing.unit)}</div>
                ))}
              </div>
            )}
            {cancelModal.production && !cancelModal.production.inventory_deducted && (
              <div style={{ backgroundColor: '#fef9c3', border: '1px solid #fde047', borderRadius: '8px', padding: '10px', fontSize: '0.82rem', color: '#854d0e' }}>
                Inventory was not deducted for this production — nothing will be returned.
              </div>
            )}
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid #e2e8f0' }}>
            <Button variant="outline-secondary" size="sm" onClick={() => setCancelModal({ show: false, production: null })}>
              Keep Production
            </Button>
            <Button
              variant="danger" size="sm"
              onClick={() => {
                if (cancelModal.production) updateProductionStatus(cancelModal.production.id, 'cancelled');
                setCancelModal({ show: false, production: null });
              }}
            >
              Confirm Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ── Start Production Modal ───────────────────────────── */}
        <Modal show={showStartModal} onHide={() => setShowStartModal(false)} centered>
          <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0' }}>
            <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>
              <PlayIcon style={{ width: '18px', height: '18px', marginRight: '8px', color: '#059669' }} />
              Start Production
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Production Name</Form.Label>
              <Form.Control
                size="sm"
                value={prodName}
                onChange={e => setProdName(e.target.value)}
                placeholder="e.g. Morning Bake — Sourdough Loaves"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Production Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={prodDate}
                onChange={e => setProdDate(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Responsible Staff Member</Form.Label>
              <Form.Select
                size="sm"
                value={prodResponsible}
                onChange={e => setProdResponsible(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">— Unassigned —</option>
                {staffMembers.map(s => (
                  <option key={s.id} value={s.id}>
                    {`${s.first_name} ${s.last_name}`.trim() || s.username}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            {result && (
              <div style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '12px', fontSize: '0.82rem', color: '#475569' }}>
                <strong style={{ color: '#1e293b', display: 'block', marginBottom: '6px' }}>Ingredients that will be deducted from inventory:</strong>
                {result.ingredient_totals.filter(i => !i.is_starter && i.ingredient_id).map((i, idx) => (
                  <div key={idx}>{i.name} — {fmtQty(i.quantity, i.unit)}</div>
                ))}
                {result.ingredient_totals.filter(i => !i.ingredient_id).length > 0 && (
                  <div className="text-muted mt-1" style={{ fontStyle: 'italic' }}>
                    Note: items without a warehouse ingredient ID will not deduct stock.
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid #e2e8f0' }}>
            <Button variant="outline-secondary" size="sm" onClick={() => setShowStartModal(false)} disabled={productionLoading}>
              Cancel
            </Button>
            <Button
              variant="success" size="sm" disabled={productionLoading || !prodDate}
              onClick={async () => {
                const res = await startProduction(prodDate, prodName, prodResponsible || null);
                setShowStartModal(false);
                if (res?.production) {
                  setLastCreatedProduction({ id: res.production.id, name: res.production.name || prodName });
                  setProductionStarted(true);
                  setTimeout(() => productionsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
                }
              }}
            >
              {productionLoading
                ? <><Spinner size="sm" animation="border" className="me-1" />Starting…</>
                : <><PlayIcon style={{ width: '14px', height: '14px', marginRight: '5px' }} />Confirm Start Production</>
              }
            </Button>
          </Modal.Footer>
        </Modal>

      </Container>
    </CompanyLayout>
  );
}
